import React, { useEffect, useState } from "react";
import CustomInput from "../components/CustomInput";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as yup from "yup";
import Dropzone from "react-dropzone";
import { useDispatch, useSelector } from "react-redux";
import { Select, Progress } from "antd";

// ICONS
import { LuUpload } from "react-icons/lu";
import { RiVideoUploadLine } from "react-icons/ri";

import {
  uploadImg,
  uploadVideo,
  delImg,
  deleteVideo,
} from "../features/upload/uploadSlice";

import {
  createProject,
  getAProject,
  updateAProject,
  resetState,
} from "../features/project/projectSlice";

/* ===================== CONSTANTS ===================== */
const INDUSTRIES = [
  "Automotive",
  "Creative",
  "Media",
  "Tourism",
  "Tech",
  "Fashion",
  "Non-Profit",
];

const CATEGORIES = [
  "Web Design",
  "Digital Marketing",
  "Graphic Design",
  "Logo Design & Branding",
  "Photography",
  "Video",
];

/* ===================== VALIDATION ===================== */
const schema = yup.object().shape({
  title: yup.string().required("Title is required"),
  type: yup.string().required("Project type is required"),
  industry: yup.string().required("Industry is required"),
  categories: yup.array().min(1, "Select at least one category"),
  tag: yup.string().required("Tag is required"),
});

/* ===================== COMPONENT ===================== */
const AddProject = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const projectSlug = location.pathname.split("/")[3];
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [videoRemoved, setVideoRemoved] = useState(false);

   const { images, video, progress } = useSelector((state) => state.upload);
  const { projectData, isSuccess, isError } = useSelector(
    (state) => state.project
  );

  /* ===================== FETCH PROJECT ===================== */
  useEffect(() => {
    if (projectSlug) {
      dispatch(getAProject(projectSlug));
    } else {
      dispatch(resetState());
    }
  }, [projectSlug, dispatch]);

  /* ===================== STATUS HANDLING ===================== */
useEffect(() => {
  if (submitted && isSuccess) { // only react if user submitted
    toast.success("Project saved successfully!");
    setTimeout(() => {
      window.location.reload();
    }, 500);
    setSubmitted(false); // reset
    setLoading(false);
  }

  if (submitted && isError) {
    toast.error("Something went wrong");
    setLoading(false);
    setSubmitted(false);
  }
}, [isSuccess, isError, submitted]);


  /* ===================== FORMIK ===================== */
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: projectData?.title || "",
      type: projectData?.type || "image",
      categories: projectData?.categories || [],
      industry: projectData?.industry ?? null,
      images: projectData?.image || [],
      video: projectData?.video || null,
      link: projectData?.link || "",
      featured: projectData?.featured || false,
      tag: projectData?.tag || null,
    },

    validationSchema: schema,
onSubmit: (values) => {
  setLoading(true);
  setSubmitted(true); // mark that user submitted

  if (values.type === "image") {
    values.images = images.length ? images : values.images;
    values.video = null;
  }

  if (values.type === "video") {
    values.video = video;
    values.images = [];
  }

  if (projectSlug) {
    dispatch(updateAProject({ slug: projectSlug, projectData: values }));
  } else {
    dispatch(createProject(values));
  }
},

  });

  /* ===================== DELETE HANDLERS ===================== */
  const handleImageDelete = (id) => {
    dispatch(delImg(id));
  };

const handleVideoDelete = (id) => {
  dispatch(deleteVideo(id));
  setVideoRemoved(true); // 👈 important
  formik.setFieldValue("video", null); // clear formik too
};

  // Use existing project media if no new uploads
const displayImages = images.length ? images : projectData?.images || [];
const displayVideo = videoRemoved
  ? null
  : video?.url
  ? video
  : projectData?.video || null;

  /* ===================== UI ===================== */
  return (
    <div>
      <h3 className="mb-4 title">
        {projectSlug ? "Edit Project" : "Add Project"}
      </h3>

      <form className="d-flex flex-column gap-3" onSubmit={formik.handleSubmit}>
        {/* TITLE */}
        <CustomInput
          type="text"
          label="Project Title"
          name="title"
          onChng={formik.handleChange("title")}
          val={formik.values.title}
        />

        {/* TYPE */}
        <div className="form-group">
          <label className="mb-1 fw-semibold">Project Type</label>
          <Select
            value={formik.values.type}
            onChange={(val) => formik.setFieldValue("type", val)}
            style={{ width: "100%" }}
            options={[
              { label: "Image Project", value: "image" },
              { label: "Video Project", value: "video" },
            ]}
          />
        </div>

        {/* INDUSTRY */}
        <div className="form-group">
          <label className="mb-1 fw-semibold">Industry</label>
          <Select
            placeholder="Select Industry"
            value={formik.values.industry}
            onChange={(val) => formik.setFieldValue("industry", val)}
            style={{ width: "100%" }}
            options={INDUSTRIES.map((i) => ({ label: i, value: i }))}
          />
        </div>

        {/* CATEGORIES */}
        <div className="form-group">
          <label className="mb-1 fw-semibold">Categories</label>
          <Select
            mode="multiple"
            placeholder="Select Categories"
            value={formik.values.categories}
            onChange={(val) => formik.setFieldValue("categories", val)}
            style={{ width: "100%" }}
            options={CATEGORIES.map((c) => ({ label: c, value: c }))}
          />
        </div>

      {/* PROJECT TAG */}
      <div className="form-group">
        <label className="mb-1 fw-semibold">Project Tag  </label>

        <Select
          placeholder="Select project tag"
          value={formik.values.tag}
          onChange={(val) => formik.setFieldValue("tag", val)}
          allowClear
          style={{ width: "100%" }}
          options={[
            { label: "Featured", value: "Featured" },
            { label: "Case Study", value: "Case Study" },
          ]}
        />
      </div>



        {/* LINK */}
        <CustomInput
          type="url"
          label="Project Link"
          name="link"
          onChng={formik.handleChange("link")}
          val={formik.values.link}
        />

        {/* IMAGE UPLOAD */}
{formik.values.type === "image" && (
  <div className="bg-white p-4 border">
    <label className="mb-2 fw-semibold d-block">Project Images</label>

    <Dropzone
      onDrop={(files) => dispatch(uploadImg(files))}
      accept={{ "image/*": [] }}
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps()}
          className="dropzone d-flex align-items-center justify-content-center flex-column"
        >
          <LuUpload size={50} className="mb-2" />
          <input {...getInputProps()} />
          <p>Click or drag to upload project images</p>
        </div>
      )}
    </Dropzone>

    {progress > 0 && progress < 100 && (
      <Progress percent={progress} status="active" className="mt-2" />
    )}

    {/* Show existing or newly uploaded images */}
    <div className="showimages d-flex flex-wrap gap-3 mt-3">
      {displayImages.map((image, index) => (
        <div className="image-card position-relative" key={index}>
          <button
            type="button"
            onClick={() => handleImageDelete(image.public_id)}
            className="btn-close position-absolute"
            style={{ top: "10px", right: "10px" }}
          />
          <img src={image.url} alt="" width={150} height={150} />
        </div>
      ))}
    </div>
  </div>
)}


{formik.values.type === "video" && (
  <div className="bg-white p-4 border">
    <label className="mb-2 fw-semibold d-block">Project Video</label>

    <Dropzone
      onDrop={(files) => {
        if (!files.length) return;
        setVideoRemoved(false);
        dispatch(uploadVideo(files[0]));
        
      }}
      accept={{ "video/*": [] }}
      maxFiles={1}
    >
      {({ getRootProps, getInputProps }) => (
        <div
          {...getRootProps()}
          className="dropzone d-flex align-items-center justify-content-center flex-column"
          style={{ border: "2px dashed #ccc", padding: "30px", cursor: "pointer" }}
        >
          <RiVideoUploadLine size={50} className="mb-2" />
          <input {...getInputProps()} />
          <p>Click or drag to upload project video</p>
        </div>
      )}
    </Dropzone>

    {progress > 0 && progress < 100 && (
      <Progress percent={progress} status="active" className="mt-2" />
    )}

    {/* Show existing or newly uploaded video */}
    {displayVideo?.url && (
      <div className="mt-3 position-relative">
        <button
          type="button"
          onClick={() => handleVideoDelete(displayVideo.public_id)}
          className="btn-close position-absolute"
          style={{ top: "10px", right: "10px" }}
        />
        <video src={displayVideo.url} controls width="300" height="200" className="border" />
      </div>
    )}
  </div>
)}



    
        <button
          type="submit"
          className="btn btn-success my-4"
          disabled={loading}
        >
          {loading
            ? "Processing..."
            : projectSlug
            ? "Update Project"
            : "Add Project"}
        </button>
      </form>
    </div>
  );
};

export default AddProject;
